// Min-max decimation utilities for waveform rendering.
// Split the incoming Float32Array (time-domain samples, range roughly [-1, 1])
// into "width" buckets and compute the min & max sample for each bucket.
// This keeps extreme peaks while reducing draw cost.

export interface MinMaxPair { min: number; max: number }

export function getMinMaxPairs(data: Float32Array, width: number): MinMaxPair[] {
  const result: MinMaxPair[] = [];
  if (!data || data.length === 0 || width <= 0) return result;
  const n = Math.max(1, width);
  const bucketSize = data.length / n;
  for (let x = 0; x < n; x++) {
    let start = Math.floor(x * bucketSize);
    let end = (x === n - 1) ? data.length : Math.floor((x + 1) * bucketSize);
    if (end <= start) end = Math.min(start + 1, data.length); // ensure at least one sample
    let min = Infinity;
    let max = -Infinity;
    for (let i = start; i < end; i++) {
      const v = data[i];
      if (v < min) min = v;
      if (v > max) max = v;
    }
    if (min === Infinity) { min = 0; max = 0; }
    result.push({ min, max });
  }
  return result;
}

// Optional helper: normalize a pair list (not required now, but future‑proofing)
export function clampPairs(pairs: MinMaxPair[], lo = -1, hi = 1): MinMaxPair[] {
  return pairs.map(p => ({
    min: Math.max(lo, Math.min(hi, p.min)),
    max: Math.max(lo, Math.min(hi, p.max)),
  }));
}
